import * as React from 'react';
import {isValidElementType} from 'react-is';

import {emptyObject} from '@packages/utilities/fp/utils';

/**
 * Checks to see if a prop should be rendered using the React
 * render prop pattern.
 */
export const isRenderProp = (prop: any) => typeof prop === 'function';

/**
 * Utility to handle render functions (children).
 */
export const renderChildren = (
  props: any = emptyObject,
  children?: any
): React.ReactNode | null => {
  if (isRenderProp(children)) return children(props) || null;
  return children || null;
};

/**
 * Renders out a React Component given either a React Component or a function which returns a React Component.
 * Functional elements can be given an optional props object for their use.
 *
 * This is useful for creating a component-as-prop pattern, such as the one used to create `Prefix` and `Suffix` in `Button`
 *
 * # Usage
 *
 * ```jsx
 * const SomeButton = ({ Prefix }) => (
 *  <button>{renderElement(Prefix)} Button Text</button>
 * )
 *
 * const SomeIcon = ({ color: 'red' }) => <Icon color={color}/>
 * const Usage = () => {
 *   <>
 *     <SomeButton Prefix={SomeIcon}  /> // SomeIcon's 'color' prop will be red
 *     <SomeButton Prefix={<SomeIcon color='blue'/>} /> // SomeIcon's 'color' prop will be blue
 *   </>
 * }
 * ```
 */
export const renderElement = (
  Element: any,
  props: Record<any, any> = emptyObject
): undefined | React.ReactNode => {
  if (React.isValidElement(Element) || typeof Element === 'string')
    // @ts-expect-error - TS2769 - No overload matches this call.
    return React.cloneElement(Element, props);

  if (isValidElementType(Element)) {
    return <Element {...props} />;
  }

  if (typeof Element === 'function') {
    const RenderedElement = Element(props);
    if (React.isValidElement(RenderedElement)) return RenderedElement;
  }
};
